import { BlockEditProps, registerBlockType } from "@wordpress/blocks";
import React, { useEffect } from "react";
import config, { type BlockAttributes } from "./block.json";
import { InnerBlocks, RichText } from "@wordpress/block-editor";
import { useBlockProps, useInnerBlocksProps } from "@wordpress/block-editor";
import { SVG, Path } from "@wordpress/components";

export default function ReadMoreEditor(props: BlockEditProps<BlockAttributes>) {
  const { attributes, setAttributes, clientId } = props;

  // Generate a unique ID based on the block's clientId if it doesn't exist
  useEffect(() => {
    if (!attributes.id) {
      setAttributes({
        id: `read-more-${clientId.slice(0, 8)}`,
      });
    }
  }, [attributes.id, clientId, setAttributes]);

  const blockProps = useBlockProps({
    className: "read-more",
  });

  const innerBlocksProps = useInnerBlocksProps(
    {
      className: "accordion-body",
    },
    {}
  );

  return (
    <div {...blockProps}>
      <div className="accordion-item" id={attributes.id}>
        <div className="accordion-header">
          <button className="accordion-button collapsed" type="button">
            <div className="d-flex flex-column flex-lg-row">
              <p className="read-more mb-0">
                <RichText
                  value={attributes.buttonText || ""}
                  onChange={(newText) => setAttributes({ buttonText: newText })}
                  placeholder="MEHR LESEN"
                />
              </p>
            </div>

            <div className="ms-3 chevron">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 18 18"
                id="Chevron-Downward--Streamline-Outlined-Expansion"
                height="20"
                width="20"
              >
                <g id="chevron-downward">
                  <path
                    id="Union"
                    fill="currentColor"
                    d="m1.49982 5.9156625 1.3312499999999998 -1.3312499999999998 6.16878 6.1687125 6.168749999999999 -6.1687125 1.3312499999999998 1.3312499999999998 -7.5 7.4999625000000005L1.49982 5.9156625Z"
                    stroke-width="1"
                  ></path>
                </g>
              </svg>
            </div>
          </button>
        </div>
        <div className="accordion-collapse">
          <div {...innerBlocksProps} />
        </div>
      </div>
    </div>
  );
}

registerBlockType(config.name, {
  ...config,

  icon() {
    return (
      <SVG viewBox="0 0 24 24">
        <Path d="M5 20.5a2 2 0 0 1-2-2v-13a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2Zm-.5-2a.5.5 0 0 0 .5.5h14a.5.5 0 0 0 .5-.5v-7.563A2 2 0 0 1 19 11H5a2 2 0 0 1-.5-.063Zm.5-9h14a.5.5 0 0 0 .5-.5V5.5A.5.5 0 0 0 19 5H5a.5.5 0 0 0-.5.5V9a.5.5 0 0 0 .5.5ZM6 17v-1.5h6.5V17Zm0-3v-1.5h10.5V14Zm9.47-5.47-2-2 1.06-1.06L16 6.939l1.47-1.469 1.06 1.06-2 2a.751.751 0 0 1-1.061 0ZM6 8V6.5h6.5V8Z" />
      </SVG>
    );
  },

  edit: ReadMoreEditor,

  save(props) {
    const blockProps = useBlockProps.save({
      className: "read-more",
    });

    const id = props.attributes.id || "";

    return (
      <div {...blockProps}>
        <div id={id} className="accordion-item">
          <div className="accordion-header" id={`${id}-heading`}>
            <button
              className="accordion-button collapsed"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#${id}-content`}
              aria-expanded="false"
              aria-controls={`${id}-content`}
            >
              <div className="d-flex flex-column flex-lg-row">
                <p className="mb-0 read-more">{props.attributes.buttonText}</p>
              </div>
              <div className="ms-3 chevron">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 18 18"
                  id="Chevron-Downward--Streamline-Outlined-Expansion"
                  height="20"
                  width="20"
                >
                  <g id="chevron-downward">
                    <path
                      id="Union"
                      fill="currentColor"
                      d="m1.49982 5.9156625 1.3312499999999998 -1.3312499999999998 6.16878 6.1687125 6.168749999999999 -6.1687125 1.3312499999999998 1.3312499999999998 -7.5 7.4999625000000005L1.49982 5.9156625Z"
                      stroke-width="1"
                    ></path>
                  </g>
                </svg>
              </div>
            </button>
          </div>
          <div
            id={`${id}-content`}
            className="accordion-collapse collapse"
            aria-labelledby={`${id}-heading`}
            data-bs-parent=".accordion"
          >
            <div className="accordion-body">
              <InnerBlocks.Content />
            </div>
          </div>
        </div>
      </div>
    );
  },
});
